import axios from 'axios';
import Auth from '../services/auth';

const auth = Auth.getInstance();

//const waitAfterCommandInMs = 500;

export function authorizationHeaders() {
  if (auth.isAuthenticated()) {
    const access_token = auth.getAccessToken();
    return {
      'Authorization': 'Bearer ' + access_token
    };
  }
  return {};
}

export function query(url, filter, actionType, successType, failureType, pagination) {
  return ((pagination && queryPage) || queryAll)(url, filter, actionType, successType, failureType, pagination);
}

export function queryOne(url, filter, actionType, successType, failureType) {
  return async function(dispatch) {
    dispatch({
      type: actionType,
      payload: filter,
    });
    try {
      const res = await axios.get(`${url}/findOne?filter=${encodeURIComponent(JSON.stringify(filter))}&_=${Date.now()}`, {
        headers: authorizationHeaders()
      });
      dispatch({
        type: successType,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: failureType,
        payload: err
      });
      throw err;
    }
  };
}

export function queryAll(url, filter, actionType, successType, failureType) {
  filter = {
    ...filter,
    limit: 0,
  };
  return async function(dispatch) {
    dispatch({
      type: actionType,
      payload: filter,
    });
    try {
      const res = await axios.get(`${url}?filter=${encodeURIComponent(JSON.stringify(filter))}&_=${Date.now()}`, {
        headers: authorizationHeaders()
      });
      const {data} = res;
      dispatch({
        type: successType,
        payload: {data},
      });
    } catch (err) {
      dispatch({
        type: failureType,
        payload: err
      });
      throw err;
    }
  };
}

export function queryPage(url, filter, actionType, successType, failureType, pagination) {
  const {current, pageSize} = pagination;
  filter = {
    ...filter,
    skip: pageSize * (current - 1),
    limit: pageSize,
    paging: true,
  };
  return async function(dispatch) {
    dispatch({
      type: actionType,
      payload: filter,
    });
    try {
      const res = await axios.get(`${url}?filter=${encodeURIComponent(JSON.stringify(filter))}&_=${Date.now()}`, {
        headers: authorizationHeaders()
      });
      const {results: data, total} = res.data;
      dispatch({
        type: successType,
        payload: {current, pageSize, total, data}
      });
    } catch (err) {
      dispatch({
        type: failureType,
        payload: err
      });
      throw err;
    }
  };
}

export function querySearch(url, actionType, successType, failureType, searchFilterMap = null, options = {}) {
  return function({pagination, filters, sorter, searchFilter}) {
    const {current, pageSize} = pagination;
    const filter = {
      skip: pageSize * (current - 1),
      limit: pageSize,
      paging: true,
    };
    const filtersEntries = Object.entries({...options.filters, ...filters});
    if (filtersEntries.length > 0) {
      filter.where = {
        and: filtersEntries.reduce((cur, [key, value]) => {
          if (value === undefined) return cur;
          if (Array.isArray(value)) {
            if (value.length === 1) {
              value = {eq: value[0]};
            } else if (value.length > 1) {
              value = {inq: value};
            } else {
              return cur;
            }
          }
          let out;
          if (options && options.filterMapper) {
            out = options.filterMapper(key, value);
          } else {
            out = {[key]: value};
          }
          return [...cur, out];
        }, [])
      };
    }
    if (sorter && sorter.field) {
      filter.order = `${sorter.field} ${sorter.order === 'descend' ? 'DESC' : 'ASC'}`;
    }
    if (!filter.order && options.defaultOrder) {
      filter.order = options.defaultOrder;
    }
    let extraQueryString = '';
    if (searchFilter) {
      if (searchFilterMap) {
        const terms = searchFilter.split(' ');
        let termFilters;
        if (typeof searchFilterMap === 'string') {
          termFilters = terms.map(x => ({[searchFilterMap]: {ilike: `%${x}%`}}));
        } else if (typeof searchFilterMap === 'function') {
          termFilters = terms.map(searchFilterMap);
        } else if (Array.isArray(searchFilterMap)) {
          termFilters = terms.map(term => ({or: searchFilterMap.map(field => ({[field]: {ilike: `%${term}%`}}))}));
        }
        if (filtersEntries.length > 0) {
          filter.where.and = filter.where.and.concat(termFilters);
        } else {
          filter.where = {and: termFilters};
        }
      } else {
        extraQueryString = `&terms=${searchFilter}`;
      }
    }
    return async function(dispatch) {
      dispatch({
        type: actionType,
        payload: {filters, sorter, pagination, searchFilter},
      });
      try {
        const res = await axios.get(`${url}?filter=${encodeURIComponent(JSON.stringify(filter))}${extraQueryString}&_=${Date.now()}`, {
          headers: authorizationHeaders()
        });
        const {results, total} = res.data;
        dispatch({
          type: successType,
          payload: {results, pagination: {current, pageSize, total}}
        });
      } catch (err) {
        dispatch({
          type: failureType,
          payload: err
        });
        throw err;
      }
    };
  };
}

export function command(url, actionType, successType, failureType) {
  return function(payload) {
    return async function(dispatch) {
      dispatch({
        type: actionType,
        payload: payload,
      });
      try {
        const res = await axios.post(url, payload, {headers: authorizationHeaders()});
        dispatch({
          type: successType,
          payload: res.data
        });
        //await delay(waitAfterCommandInMs);
      } catch (err) {
        dispatch({
          type: failureType,
          payload: err
        });
        throw err;
      }
    };
  };
}

/**
 * Build a batch action function
 * @param {string} url
 * @param {string} actionType start batch action sent once
 * @param {string} successType success sent 0 or more times
 * @param {string} failureType failure sent 0 or more times
 * @param {function} rowKey
 * @returns {function(*=): Function}
 */
export function batchCommand(url, actionType, successType, failureType, rowKey) {
  return function(payloads) {
    payloads = payloads.map(payload => ({key: rowKey(payload), payload}));
    return async function(dispatch) {
      if (!payloads.length) return;
      dispatch({
        type: actionType,
        payload: payloads,
      });
      await Promise.all(payloads.map(({key, payload}) => (async function() {
        try {
          const res = await axios.post(url, payload, {headers: authorizationHeaders()});
          dispatch({
            type: successType,
            key,
            payload: res.data,
          });
        } catch (err) {
          dispatch({
            type: failureType,
            key,
            payload: err,
          });
        }
      })()));
      //await delay(waitAfterCommandInMs);
    };
  };
}



// WEBPACK FOOTER //
// ./src/utils/api.js